import React, { FunctionComponent, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { UserPermissionAction } from "avvir";

import getInspectionMode from "../../../services/getters/selection_getters/get_inspection_mode";
import inspectionModes, { InspectionMode } from "../../../models/domain/enums/inspection_modes";
import switchExplorerMode from "../../../actions/viewer_page/switch_explorer_mode";
import ToggleButton from "../../buttons/toggle_button";
import ToggleButtonGroup from "../../buttons/toggle_button_group";
import { usePermissionService } from "../../../queries/user/use_permission_service";

import type { Dispatch } from "type_aliases";
import type { ReduxStore } from "../../../services/reducers/root_reducer";

export type Props = ReturnType<typeof mapStateToProps>
                    & ReturnType<typeof mapDispatchToProps>;

// The toggle's order, which differs from the mode panel selector's — inspection_modes' own order
// is the legacy dropdown's and reordering it would move BIM Prep there too.
const modeOrder = [
  InspectionMode.basicBim,
  InspectionMode.progress,
  InspectionMode.inspect,
  InspectionMode.bimPrep,
];

// The mode switch the mode panel's "Inspection Mode" selector used to hold. This is the only
// control that carries the mode's panel and reality capture defaults, so a mode arriving with a
// saved view leaves both alone.
export const NavBarSecondaryModes: FunctionComponent<Props> = ({ inspectionMode, switchExplorerMode }) => {
  const permissionService = usePermissionService();
  const canUseBimPrep = permissionService.isPermittedForCurrentProject(UserPermissionAction.USE_BIM_PREP_MODE);

  // Re-selecting the current mode would still reset colorMode to that mode's default, throwing
  // away a colour the user picked in the viewer. The old dropdown never fired for an unchanged value.
  const selectMode = useCallback((event, mode: InspectionMode) => {
    if (mode !== inspectionMode) {
      switchExplorerMode(mode);
    }
  }, [inspectionMode, switchExplorerMode]);

  // Same gate as the mode panel selector, so an unpermitted user sees the design's three-mode toggle.
  const modeButtons = useMemo(() => {
    return modeOrder
      .filter(mode => mode !== InspectionMode.bimPrep || canUseBimPrep)
      .map(mode => {
        const option = inspectionModes.find(({ value }) => value === mode);

        return <ToggleButton
          className="NavBarSecondaryModes-mode"
          key={mode}
          value={mode}
          disableRipple
        >
          {option.shortDisplayName}
        </ToggleButton>;
      });
  }, [canUseBimPrep]);

  return <ToggleButtonGroup
    className="NavBarSecondaryModes"
    aria-label="Mode"
    value={inspectionMode}
    onChange={selectMode}
  >
    {modeButtons}
  </ToggleButtonGroup>;
};

NavBarSecondaryModes.defaultProps = {
  inspectionMode: InspectionMode.basicBim,
};

const mapStateToProps = (state: ReduxStore, props: Record<string, never>) => ({
  inspectionMode: getInspectionMode(state, props),
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => bindActionCreators({
  switchExplorerMode,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(NavBarSecondaryModes);
